import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Sprout, Users, BookOpen, ShieldCheck, Sun, Wheat, Snowflake } from "lucide-react";
import { MaskedLines, Reveal, ChapterNumber } from "../components/motion";
import { usePageTitle } from "../lib/title";
import { MarqueeBand } from "../components/MarqueeBand";
import { IMAGES, marqueeItems, articles } from "../data/content";

const chapters = [
  {
    n: "01",
    icon: Sprout,
    title: "Benefits should be understood, not endured.",
    body: "My mission is simple: give people the knowledge and tools to optimize their health benefits. I translate the jargon, lay out the real trade-offs, and leave you confident — never confused.",
  },
  {
    n: "02",
    icon: Users,
    title: "Individuals, families, and the employers who carry them.",
    body: "From a family's first marketplace plan to an employer questioning whether their PEO is earning its keep, I serve both sides of the benefits table — because I've sat in both chairs.",
  },
  {
    n: "03",
    icon: ShieldCheck,
    title: "Independent. Texas-licensed agent. Invested for the long haul.",
    body: "I'm not here for a quick dollar. OneHarvestCo. Benefits is being built as a lasting legacy of genuinely trusted relationships — clients who know, unequivocally, that their success is the only driver of mine.",
  },
];

export default function Home() {
  usePageTitle();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-screen items-center overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0 scale-110">
          <img
            src={IMAGES.hero}
            alt="Golden wheat field at sunset"
            className="h-full w-full object-cover"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/80 to-cream/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-cream/30" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-36 lg:px-10">
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-terra"
            data-testid="hero-kicker"
          >
            Group Health, PEO &amp; Individual Coverage · The Woodlands, TX
          </motion.h1>

          <MaskedLines
            as="p"
            className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-7xl lg:text-8xl"
            lines={[
              "Good benefits,",
              <span key="l2">
                <em className="text-terra">well</em> harvested.
              </span>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.6 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg"
            data-testid="hero-subcopy"
          >
            OneHarvestCo. Benefits helps individuals and families understand and choose the right health
            coverage, and helps local employers build, renew, and optimize their group health plans or PEO
            arrangements. Plain language, patience, and a local owner you can call directly.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact?type=consultation"
              data-testid="hero-consultation-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-8 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-1 hover:bg-moss"
            >
              Request a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-services-cta"
              className="group inline-flex items-center gap-2 rounded-full border border-forest/30 px-8 py-3.5 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:text-terra"
            >
              Explore Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-16 flex flex-wrap gap-x-12 gap-y-4 border-t border-forest/10 pt-8"
            data-testid="hero-stats"
          >
            {[
              ["Local", "The Woodlands, Houston & surrounding communities"],
              ["Two Tables", "Individuals & employer benefits"],
              ["Year-Round", "Support beyond enrollment season"],
            ].map(([label, desc]) => (
              <div key={label}>
                <p className="font-serif text-2xl font-medium text-forest">{label}</p>
                <p className="mt-1 text-xs tracking-wide text-ink/60">{desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <MarqueeBand items={marqueeItems} />

      {/* MANIFESTO */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="manifesto-section">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">The Manifesto</p>
          <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl lg:text-6xl">
            Why I planted <em className="text-terra">OneHarvestCo.</em>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70" data-testid="manifesto-modern-options">
            And one belief that shapes all of it: the newer care and payment models rarely make it into the conversation, and you deserve to hear about them. I put them on the table alongside the familiar
            choices.
          </p>
        </Reveal>

        <div className="mt-16 space-y-0 lg:mt-24">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={0.05 * i}>
              <div
                className={`grid gap-8 border-t border-border py-14 lg:grid-cols-12 lg:gap-12 lg:py-20 ${
                  i === chapters.length - 1 ? "border-b" : ""
                }`}
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <div className="lg:col-span-2">
                  <ChapterNumber n={c.n} />
                </div>
                <div className="lg:col-span-5">
                  <c.icon className="mb-6 h-7 w-7 text-gold" strokeWidth={1.5} />
                  <h3 className="font-serif text-2xl font-medium leading-snug tracking-tight text-forest sm:text-3xl">
                    {c.title}
                  </h3>
                </div>
                <div className="flex items-end lg:col-span-5">
                  <p className="text-base leading-relaxed text-ink/70">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* IMAGE + SERVICES BRIDGE */}
      <section className="bg-sand/70" data-testid="home-services-bridge">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32">
          <Reveal className="lg:col-span-5">
            <div className="clipped-frame overflow-hidden">
              <motion.img
                src={IMAGES.familyBridge}
                alt="A family seen from behind, holding hands as they walk a path toward the sunset"
                className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[540px]"
                data-testid="bridge-image"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40">
              For every season of life
            </p>
          </Reveal>
          <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
            <Reveal>
              <ChapterNumber n="04" />
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
                Two fields, <em className="text-terra">one harvest</em>.
              </h2>
              <p className="mt-8 max-w-lg text-base leading-relaxed text-ink/70">
                Whether you're choosing coverage for your family or sorting out benefits for your team, the
                work is the same: understand the options, weigh the real costs, and choose what bears
                fruit when it's needed most.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/individuals"
                  data-testid="bridge-individuals-link"
                  className="group inline-flex items-center gap-2 rounded-full bg-forest px-7 py-3 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss"
                >
                  Individuals &amp; Families
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/employers"
                  data-testid="bridge-employers-link"
                  className="group inline-flex items-center gap-2 rounded-full border border-forest/30 px-7 py-3 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-0.5 hover:border-gold hover:text-terra"
                >
                  Employers
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
              <Link
                to="/group-benefits-the-woodlands"
                data-testid="bridge-woodlands-link"
                className="group mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-terra transition-colors duration-300 hover:text-forest"
              >
                Group benefits in The Woodlands
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EDUCATION PREVIEW */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32" data-testid="home-education-preview">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra">The Knowledge Field</p>
            <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              Learn before <em className="text-terra">you leap</em>.
            </h2>
          </div>
          <Link
            to="/education"
            data-testid="education-view-all-link"
            className="group inline-flex items-center gap-2 text-sm font-medium text-forest"
          >
            <span className="link-underline">View all articles</span>
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-12">
          {articles.slice(0, 3).map((a, i) => (
            <Reveal key={a.slug} delay={0.08 * i} className={i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : "lg:col-span-12"}>
              <Link
                to={`/education/${a.slug}`}
                data-testid={`home-article-card-${a.slug}`}
                className={`group flex h-full flex-col justify-between border border-border bg-cream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 hover:bg-sand/50 lg:p-10 ${
                  i === 2 ? "lg:flex-row lg:items-center lg:gap-12" : ""
                }`}
              >
                <div>
                  <div className="flex items-center gap-4">
                    <BookOpen className="h-4 w-4 text-gold" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
                      {a.category} — {a.readTime}
                    </span>
                  </div>
                  <h3 className="mt-5 font-serif text-2xl font-medium leading-snug tracking-tight text-forest transition-colors group-hover:text-terra sm:text-3xl">
                    {a.title}
                  </h3>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink/65">{a.excerpt}</p>
                </div>
                <ArrowUpRight className="mt-8 h-5 w-5 shrink-0 text-forest/40 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-gold" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SEASONS */}
      <section className="border-t border-border" data-testid="seasons-section">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="seasons-kicker">
              Through the Seasons
            </p>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-forest sm:text-5xl">
              One relationship, <em className="text-terra">tended all year</em>.
            </h2>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ink/70">
              Benefits aren&rsquo;t a once-a-year decision, and neither is my commitment. Here&rsquo;s what a
              year with OneHarvestCo. actually looks like.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Sprout, season: "Spring", title: "New Growth", body: "Life changes mid-year — marriages, babies, new jobs. I help you through special enrollment periods and adjust coverage as your world shifts." },
              { icon: Sun, season: "Summer", title: "Tending", body: "Mid-year reviews: HSA checkups, network changes, and renewal prep for employers — small adjustments before they become problems." },
              { icon: Wheat, season: "Fall", title: "The Harvest", body: "Open enrollment is harvest season. Full plan reviews and side-by-side comparisons of the options available to you for the year ahead." },
              { icon: Snowflake, season: "Winter", title: "Stewardship", body: "New plan year kickoff: ID cards, benefits walkthroughs, deductible planning, and a real person to call when claims get confusing." },
            ].map((s, i) => (
              <Reveal key={s.season} delay={0.08 * i}>
                <div
                  className="group h-full border border-border p-8 transition-all duration-500 hover:-translate-y-1 hover:border-gold/60 hover:bg-sand/50"
                  data-testid={`season-card-${s.season.toLowerCase()}`}
                >
                  <s.icon className="h-6 w-6 text-gold transition-transform duration-500 group-hover:scale-110" strokeWidth={1.5} />
                  <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.35em] text-terra">{s.season}</p>
                  <h3 className="mt-3 font-serif text-2xl font-medium tracking-tight text-forest">{s.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-ink/65">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative overflow-hidden bg-forest" data-testid="home-cta-band">
        <img src={IMAGES.texture} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold">Begin the Conversation</p>
            <h2 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              The best time to understand your benefits was at enrollment.
              <em className="text-gold"> The second-best time is now.</em>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/70" data-testid="cta-band-legacy-line">
              I&rsquo;m not here for the quick enrollment. I&rsquo;m here to earn your trust in every season —
              and to build something that lasts.
            </p>
            <Link
              to="/contact?type=consultation"
              data-testid="cta-band-consultation-button"
              className="group mt-12 inline-flex items-center gap-2 rounded-full bg-gold px-9 py-4 text-sm font-medium text-forest transition-all duration-300 hover:-translate-y-1 hover:bg-cream"
            >
              Request a Consultation
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
